import { css } from 'styled-components';

export type Av2Colors = {
  pageBg: string;
  surface: string;
  surfaceElevated: string;
  sidebarBg: string;
  inputBg: string;
  inputBgGradientStart: string;
  inputBgGradientEnd: string;
  hoverBg: string;
  overlay: string;
  border: string;
  borderSubtle: string;
  borderStrong: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textLight: string;
  textOnAccent: string;
  primary: string;
  primaryDark: string;
  primaryDarker: string;
  primaryHover: string;
  primaryLight: string;
  primaryMuted: string;
  primaryTint: string;
  primaryTintStrong: string;
  primaryTintBg: string;
  primaryBorder: string;
  primaryGlow: string;
  primaryGlowStrong: string;
  primaryFocusRing: string;
  accentText: string;
  accentTextLight: string;
  accentPink: string;
  userBubble: string;
  success: string;
  successBg: string;
  successTint: string;
  warning: string;
  error: string;
  errorBg: string;
  errorBorder: string;
  shadowWarm: string;
  shadowAccent: string;
  shadowAccentStrong: string;
  responseText: string;
  responseMuted: string;
  modalBackdrop: string;
};

/** Ask Vantage light palette — blue / violet / teal accents.
 * Green is reserved for the user (question) bubble only. */
export const lightColors: Av2Colors = {
  pageBg: '#F8F7F5',
  surface: '#EEECEA',
  surfaceElevated: '#FFFFFF',
  sidebarBg: '#FFFFFF',
  inputBg: '#FFFFFF',
  inputBgGradientStart: '#FFFFFF',
  inputBgGradientEnd: '#F5F6FA',
  hoverBg: '#EEF0F4',
  overlay: 'rgba(29, 29, 29, 0.35)',
  border: '#E5E6EA',
  borderSubtle: '#F0F1F4',
  borderStrong: '#D4D6DD',
  textPrimary: '#111111',
  textSecondary: '#5B5B5B',
  textMuted: '#959595',
  textLight: '#B0B0B0',
  textOnAccent: '#FFFFFF',
  primary: '#3B82F6',
  primaryDark: '#2D6BD4',
  primaryDarker: '#2057B0',
  primaryHover: '#4B8DF8',
  primaryLight: '#8B6FE8',
  primaryMuted: '#DCE7FD',
  primaryTint: 'rgba(59, 130, 246, 0.10)',
  primaryTintStrong: 'rgba(59, 130, 246, 0.18)',
  primaryTintBg: '#EEF3FF',
  primaryBorder: '#A9C5F5',
  primaryGlow: 'rgba(59, 130, 246, 0.22)',
  primaryGlowStrong: 'rgba(59, 130, 246, 0.32)',
  primaryFocusRing: 'rgba(59, 130, 246, 0.20)',
  accentText: '#3B82F6',
  accentTextLight: '#6366F1',
  accentPink: '#7C5CD6',
  userBubble: '#306643',
  success: '#0D8A6A',
  successBg: '#E8F5F1',
  successTint: 'rgba(13, 138, 106, 0.12)',
  warning: '#FFB800',
  error: '#FF3A5C',
  errorBg: '#FFF0F3',
  errorBorder: '#FF3A5C',
  shadowWarm: 'rgba(17, 17, 17, 0.08)',
  shadowAccent: 'rgba(59, 130, 246, 0.20)',
  shadowAccentStrong: 'rgba(59, 130, 246, 0.28)',
  responseText: '#111111',
  responseMuted: '#5B5B5B',
  modalBackdrop: 'rgba(17, 17, 17, 0.45)',
};

/** Dark palette — blue / violet / teal accents.
 * Green is reserved for the user (question) bubble only. */
export const darkColors: Av2Colors = {
  pageBg: '#1A1B1E',
  surface: '#242629',
  surfaceElevated: '#2E3035',
  sidebarBg: '#161719',
  inputBg: '#33363B',
  inputBgGradientStart: '#383B41',
  inputBgGradientEnd: '#2C2E33',
  hoverBg: '#3A3D43',
  overlay: 'rgba(0, 0, 0, 0.58)',
  border: '#54575E',
  borderSubtle: '#42454B',
  borderStrong: '#666A72',
  textPrimary: '#EEEEEE',
  textSecondary: '#C8C8C8',
  textMuted: '#9E9E9E',
  textLight: '#828282',
  textOnAccent: '#FFFFFF',
  primary: '#5B9BFF',
  primaryDark: '#8FB8FF',
  primaryDarker: '#3B82F6',
  primaryHover: '#6FA9FF',
  primaryLight: '#9B86F0',
  primaryMuted: '#1F2A47',
  primaryTint: 'rgba(91, 155, 255, 0.18)',
  primaryTintStrong: 'rgba(91, 155, 255, 0.30)',
  primaryTintBg: '#1E2740',
  primaryBorder: 'rgba(124, 156, 255, 0.40)',
  primaryGlow: 'rgba(91, 155, 255, 0.24)',
  primaryGlowStrong: 'rgba(91, 155, 255, 0.36)',
  primaryFocusRing: 'rgba(155, 134, 240, 0.22)',
  accentText: '#9BB8FF',
  accentTextLight: '#B7A9F5',
  accentPink: '#9B86F0',
  userBubble: '#3E8159',
  success: '#22A884',
  successBg: '#1e3d32',
  successTint: 'rgba(34, 168, 132, 0.18)',
  warning: '#FFB800',
  error: '#FF5C7A',
  errorBg: '#3d1f28',
  errorBorder: '#FF5C7A',
  shadowWarm: 'rgba(0, 0, 0, 0.42)',
  shadowAccent: 'rgba(91, 155, 255, 0.28)',
  shadowAccentStrong: 'rgba(91, 155, 255, 0.38)',
  responseText: '#F0F0F0',
  responseMuted: '#C8C8C8',
  modalBackdrop: 'rgba(0, 0, 0, 0.72)',
};

export type ThemeMode = 'light' | 'dark';

export const getAv2Colors = (mode: ThemeMode): Av2Colors =>
  mode === 'dark' ? darkColors : lightColors;

/** @deprecated Use theme.av2 via AskV2ThemeProvider — kept for gradual migration */
export const colors = lightColors;

/** Sapaad brand gradient — violet (left) → blue (right) at 270deg */
export const brandGradient = 'linear-gradient(270deg, #3B82F6 0%, #7C5CD6 100%)';

/** Ask Vantage send button — teal / blue / violet layered gradient */
export const askSendButtonBackground =
  'linear-gradient(147deg, rgba(12, 201, 191, 0) 0%, rgb(0 99 168) 50%, rgb(4 174 164) 100%), linear-gradient(147deg, rgb(118 11 151) 0%, rgb(20 36 166) 100%)';

export const askSendButtonShadow =
  'rgba(28, 136, 159, 0.5) 0px 0px 0px 2px, rgba(76, 80, 175, 0.35) 0px 2px 10px';

/** Navbar dispatches this when Ask Vantage is clicked while already on /ask */
export const ASK_VANTAGE_HOME_EVENT = 'askvantage:home';

// Brand accent endpoints (names kept for import stability; values are the
// blue/violet brand palette).
export const BRAND_PINK = '#7C5CD6';
export const BRAND_ORANGE = '#3B82F6';

/** Soft dual-tone ambient glow behind the hero input (transparent fade).
 * Teal/blue (left) blending into violet (right). */
export const heroAmbientBackground = (mode: ThemeMode): string => {
  const isDark = mode === 'dark';
  const blueCore = isDark ? 0.12 : 0.20;
  const blueMid = isDark ? 0.05 : 0.09;
  const violetCore = isDark ? 0.12 : 0.18;
  const violetMid = isDark ? 0.05 : 0.07;

  const brandWash = isDark
    ? 'linear-gradient(180deg, rgba(45, 212, 191, 0.04) 0%, transparent 40%, rgba(124, 92, 214, 0.04) 100%)'
    : 'transparent';

  return `
    radial-gradient(
      ellipse 720px 460px at 32% 52%,
      rgba(59, 130, 246, ${blueCore}) 0%,
      rgba(45, 212, 191, ${blueMid}) 45%,
      transparent 75%
    ),
    radial-gradient(
      ellipse 720px 460px at 68% 52%,
      rgba(124, 92, 214, ${violetCore}) 0%,
      rgba(124, 92, 214, ${violetMid}) 45%,
      transparent 75%
    ),
    ${brandWash}
  `.trim();
};

/** Hero input surround glow using brand blue + violet (teal ring). */
export const heroBrandGlowShadow = (
  mode: ThemeMode,
  intensity: 'rest' | 'hover' | 'focus' = 'rest'
): string => {
  const isDark = mode === 'dark';
  const scale = intensity === 'hover' ? 1.15 : intensity === 'focus' ? 1.05 : 1;
  const blueAlpha = (isDark ? 0.12 : 0.16) * scale;
  const violetAlpha = (isDark ? 0.20 : 0.24) * scale;
  const ringAlpha = isDark ? 0.24 : 0.18;

  return `
    0 0 0 1px rgba(124, 156, 255, ${ringAlpha}),
    0 0 48px rgba(59, 130, 246, ${blueAlpha}),
    0 18px 50px rgba(124, 92, 214, ${violetAlpha}),
    0 6px 22px rgba(124, 92, 214, ${violetAlpha * 0.65}),
    0 2px 10px rgba(0, 0, 0, ${isDark ? 0.45 : 0.08}),
    inset 0 1px 0 rgba(255, 255, 255, ${isDark ? 0.05 : 0.55})
  `.trim();
};

export const contentColumnPadding = css`
  padding-left: 16px;
  padding-right: 16px;

  @media (min-width: 769px) {
    padding-left: 10%;
    padding-right: 10%;
  }

  @media (min-width: 1280px) {
    padding-left: 20%;
    padding-right: 20%;
  }
`;

/**
 * Synthesis HTML is authored for a dark UI. In light mode, remap surfaces + text
 * to av2 light tokens so cards, alerts, tables, and prose all match the page theme.
 */
export const av2ResponseLightModeHtmlFix = css`
  /* ── Prose ─────────────────────────────────────────────────────────── */
  .av2-response [style*='color:#e8e8e8'],
  .av2-response [style*='color: #e8e8e8'],
  .av2-response [style*='color:#E8E8E8'],
  .av2-response [style*='color: #E8E8E8'] {
    color: ${({ theme }) => theme.av2.responseText} !important;
  }

  .av2-response [style*='color:#888'],
  .av2-response [style*='color: #888'] {
    color: ${({ theme }) => theme.av2.textMuted} !important;
  }

  .av2-response p,
  .av2-response li,
  .av2-response ul,
  .av2-response ol,
  .av2-response h1,
  .av2-response h2,
  .av2-response h3,
  .av2-response h4 {
    color: ${({ theme }) => theme.av2.responseText} !important;
  }

  .av2-response div[style*='border-bottom:1px solid #1e2130'],
  .av2-response div[style*='border-bottom: 1px solid #1e2130'] {
    color: ${({ theme }) => theme.av2.responseText} !important;
    border-bottom-color: ${({ theme }) => theme.av2.border} !important;
  }

  /* ── Metric cards (#1a1d28) → light elevated cards ─────────────────── */
  .av2-response [style*='background:#1a1d28'],
  .av2-response [style*='background: #1a1d28'] {
    background: ${({ theme }) => theme.av2.surfaceElevated} !important;
    box-shadow:
      0 0 0 1px ${({ theme }) => theme.av2.border},
      0 1px 4px ${({ theme }) => theme.av2.shadowWarm};
  }

  /* ── Insight / action / link chips (#1e1740) → light tint surfaces ─── */
  .av2-response [style*='background:#1e1740'],
  .av2-response [style*='background: #1e1740'] {
    background: ${({ theme }) => theme.av2.primaryTintBg} !important;
    border-color: ${({ theme }) => theme.av2.primaryBorder} !important;
  }

  .av2-response [style*='background:#1e1740'] [style*='color:#c4bff0'],
  .av2-response [style*='background:#1e1740'] [style*='color: #c4bff0'],
  .av2-response [style*='background:#1e1740'] [style*='color:#AFA9EC'],
  .av2-response [style*='background:#1e1740'] [style*='color: #AFA9EC'],
  .av2-response [style*='background:#1e1740'] [style*='color:#e8e8e8'],
  .av2-response [style*='background:#1e1740'] [style*='color: #e8e8e8'],
  .av2-response [style*='background: #1e1740'] [style*='color:#c4bff0'],
  .av2-response [style*='background: #1e1740'] [style*='color: #c4bff0'],
  .av2-response [style*='background: #1e1740'] [style*='color:#AFA9EC'],
  .av2-response [style*='background: #1e1740'] [style*='color: #AFA9EC'],
  .av2-response [style*='background: #1e1740'] [style*='color:#e8e8e8'],
  .av2-response [style*='background: #1e1740'] [style*='color: #e8e8e8'],
  .av2-response [style*='background:#1e1740'][style*='color:#c4bff0'],
  .av2-response [style*='background:#1e1740'][style*='color: #c4bff0'],
  .av2-response [style*='background:#1e1740'][style*='color:#AFA9EC'],
  .av2-response [style*='background:#1e1740'][style*='color: #AFA9EC'],
  .av2-response [style*='background:#1e1740'][style*='color:#e8e8e8'],
  .av2-response [style*='background:#1e1740'][style*='color: #e8e8e8'],
  .av2-response [style*='background: #1e1740'][style*='color:#c4bff0'],
  .av2-response [style*='background: #1e1740'][style*='color: #c4bff0'],
  .av2-response [style*='background: #1e1740'][style*='color:#AFA9EC'],
  .av2-response [style*='background: #1e1740'][style*='color: #AFA9EC'],
  .av2-response [style*='background: #1e1740'][style*='color:#e8e8e8'],
  .av2-response [style*='background: #1e1740'][style*='color: #e8e8e8'] {
    color: ${({ theme }) => theme.av2.accentText} !important;
  }

  .av2-response button[style*='background:#1e1740'],
  .av2-response button[style*='background: #1e1740'],
  .av2-response a[style*='background:#1e1740'],
  .av2-response a[style*='background: #1e1740'] {
    background: ${({ theme }) => theme.av2.surfaceElevated} !important;
    border-color: ${({ theme }) => theme.av2.primaryBorder} !important;
    color: ${({ theme }) => theme.av2.accentText} !important;
  }

  /* ── Warning / critical alerts (#2a1a14) → light warning banner ────── */
  .av2-response [style*='background:#2a1a14'],
  .av2-response [style*='background: #2a1a14'] {
    background: ${({ theme }) => theme.av2.errorBg} !important;
    border-left-color: ${({ theme }) => theme.av2.errorBorder} !important;
  }

  .av2-response [style*='background:#2a1a14'] [style*='color:#e8a88c'],
  .av2-response [style*='background:#2a1a14'] [style*='color: #e8a88c'],
  .av2-response [style*='background: #2a1a14'] [style*='color:#e8a88c'],
  .av2-response [style*='background: #2a1a14'] [style*='color: #e8a88c'],
  .av2-response [style*='background:#2a1a14'][style*='color:#e8a88c'],
  .av2-response [style*='background:#2a1a14'][style*='color: #e8a88c'],
  .av2-response [style*='background: #2a1a14'][style*='color:#e8a88c'],
  .av2-response [style*='background: #2a1a14'][style*='color: #e8a88c'] {
    color: ${({ theme }) => theme.av2.textSecondary} !important;
  }

  /* ── Teaser / success hints (#112423) → light success tint ─────────── */
  .av2-response [style*='background:#112423'],
  .av2-response [style*='background: #112423'] {
    background: ${({ theme }) => theme.av2.successBg} !important;
    border-left-color: ${({ theme }) => theme.av2.success} !important;
  }

  .av2-response [style*='background:#112423'] [style*='color:#5CB8A0'],
  .av2-response [style*='background:#112423'] [style*='color: #5CB8A0'],
  .av2-response [style*='background: #112423'] [style*='color:#5CB8A0'],
  .av2-response [style*='background: #112423'] [style*='color: #5CB8A0'] {
    color: ${({ theme }) => theme.av2.success} !important;
  }

  .av2-response button[style*='background:#112423'],
  .av2-response button[style*='background: #112423'] {
    background: ${({ theme }) => theme.av2.successBg} !important;
    border-color: ${({ theme }) => theme.av2.success} !important;
    color: ${({ theme }) => theme.av2.success} !important;
  }

  /* ── Form fields inside clarification blocks ───────────────────────── */
  .av2-response [style*='background:#0f1117'],
  .av2-response [style*='background: #0f1117'] {
    background: ${({ theme }) => theme.av2.inputBg} !important;
    color: ${({ theme }) => theme.av2.textPrimary} !important;
    border-color: ${({ theme }) => theme.av2.border} !important;
  }

  /* ── Inline code ───────────────────────────────────────────────────── */
  .av2-response code[style*='background:#1e1740'],
  .av2-response code[style*='background: #1e1740'] {
    background: ${({ theme }) => theme.av2.surface} !important;
    color: ${({ theme }) => theme.av2.accentTextLight} !important;
  }

  /* ── Semantic delta colors ─────────────────────────────────────────── */
  .av2-response [style*='color:#1D9E75'],
  .av2-response [style*='color: #1D9E75'],
  .av2-response [style*='color:#1d9e75'],
  .av2-response [style*='color: #1d9e75'] {
    color: #1d9e75 !important;
  }

  .av2-response [style*='color:#D85A30'],
  .av2-response [style*='color: #D85A30'],
  .av2-response [style*='color:#d85a30'],
  .av2-response [style*='color: #d85a30'] {
    color: #d85a30 !important;
  }

  .av2-response strong[style*='color:#D85A30'],
  .av2-response strong[style*='color: #D85A30'] {
    color: ${({ theme }) => theme.av2.errorBorder} !important;
  }

  .av2-response strong[style*='color:#AFA9EC'],
  .av2-response strong[style*='color: #AFA9EC'] {
    color: ${({ theme }) => theme.av2.accentTextLight} !important;
  }

  /* ── Tables ────────────────────────────────────────────────────────── */
  .av2-response table {
    color: ${({ theme }) => theme.av2.responseText} !important;
  }

  .av2-response th {
    background: ${({ theme }) => theme.av2.surface} !important;
    color: ${({ theme }) => theme.av2.textSecondary} !important;
    border-color: ${({ theme }) => theme.av2.border} !important;
  }

  .av2-response td {
    color: ${({ theme }) => theme.av2.responseText} !important;
    background: ${({ theme }) => theme.av2.surfaceElevated} !important;
    border-color: ${({ theme }) => theme.av2.border} !important;
  }

  .av2-response tr {
    background: transparent !important;
    border-color: ${({ theme }) => theme.av2.border} !important;
  }

  .av2-response thead tr[style*='background:#1a1d28'],
  .av2-response thead tr[style*='background: #1a1d28'] {
    background: ${({ theme }) => theme.av2.surface} !important;
  }

  /* ── LLM dark borders → light borders ──────────────────────────────── */
  .av2-response [style*='border:1px solid #1e2130'],
  .av2-response [style*='border: 1px solid #1e2130'],
  .av2-response [style*='border:1px solid #2a2d3a'],
  .av2-response [style*='border: 1px solid #2a2d3a'],
  .av2-response [style*='border:1px solid #3d3580'],
  .av2-response [style*='border: 1px solid #3d3580'],
  .av2-response [style*='border-bottom:2px solid #2a2d3a'],
  .av2-response [style*='border-bottom: 2px solid #2a2d3a'] {
    border-color: ${({ theme }) => theme.av2.border} !important;
  }
`;
